import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import type { CSSProperties, ReactNode } from 'react';

interface FieldProps {
  label: string;
  error?: string | undefined;
  errorId?: string | undefined;
  children: ReactNode;
}

/**
 * Field — calm form label + input slot + optional inline error.
 *
 * Vocabulary A (canonical): label at fontSize 12, T.textMuted, letterSpacing
 * 0.04em — NOT bold, NOT uppercase. This is the shared source of truth for
 * all forms in gg-web. Delete local Field copies in favour of this.
 *
 * Local copies that were replaced:
 *   - src/pages/ServiceLog.tsx (Field)
 *   - src/pages/ProjectsPage.tsx (Field)
 *   - src/components/ModificationsSection.tsx (Field)
 *
 * Vocabulary B forms (VehicleGenesisPage, VinClaimedScreen, TransferInitiate,
 * TransferAccept) were also migrated to this component — bold-uppercase labels
 * are gone from those flows.
 */
export function Field({ label, error, errorId, children }: FieldProps) {
  return (
    <label style={{ display: 'grid', gap: SPACE.sm }}>
      <span style={{ fontSize: 12, color: T.textMuted, letterSpacing: '0.04em' }}>{label}</span>
      {children}
      {error ? (
        // Render the message even without an errorId — a missing id only loses
        // the aria-describedby association, it must never swallow the error.
        <span
          {...(errorId ? { id: errorId } : {})}
          style={{ color: T.red, fontSize: 12 }}
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * Canonical input style — Vocabulary A.
 * Used with <input>, <textarea> elements inside a Field.
 */
export const inputStyle: CSSProperties = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  color: T.text,
  padding: `${SPACE.sm}px ${SPACE.md}px`,
  fontSize: 14,
  fontFamily: F.body,
  resize: 'vertical',
};

/**
 * Canonical select style — Vocabulary A.
 */
export const selectStyle: CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
};

/**
 * Genesis/review form input style — same padding but slightly shorter (minHeight: 44
 * is met by the line-height) and width: 100% / boxSizing for full-width inputs
 * in the vehicle creation flow.
 */
export const genesisInputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  padding: `${SPACE.sm}px ${SPACE.md}px`,
  color: T.text,
  fontSize: 14,
  fontFamily: F.body,
  minHeight: 44,
};
